import React from 'react';
import { FileText, Download } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { documents } from '../data/mockData';
import { toast } from 'sonner';

const Documents = () => {
  const handleDownload = (doc) => {
    // Open PDF in new tab
    window.open(doc.url, '_blank');
    toast.success(`Opening: ${doc.title}`);
  };

  return (
    <div className="min-h-screen bg-transparent">
      {/* Hero */}
      <section className="bg-gradient-to-br from-[#0a1628] via-[#1e3a5f] to-[#0a1628] text-white py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <FileText className="w-20 h-20 mx-auto mb-6 text-slate-300" />
            <h1 className="text-4xl lg:text-5xl font-bold mb-6">Documents & Resources</h1>
            <p className="text-xl text-slate-300 leading-relaxed">
              Public-safe downloadable materials for partnership discussions, government-facing positioning, 
              and technical collaboration.
            </p>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 bg-blue-900/20 backdrop-blur-sm">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-lg text-blue-100 leading-relaxed">
              This section hosts public-safe downloadable materials that support company introductions, 
              partnership discussions, and government-facing positioning. All documents are cleared for 
              public distribution and contain no sensitive technical implementation details.
            </p>
          </div>
        </div>
      </section>

      {/* Documents Grid */}
      <section className="py-16 bg-transparent">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {documents.map((doc) => (
              <Card key={doc.id} className="border-blue-500/30 hover:shadow-xl transition-all hover:border-[#1e3a5f]">
                <CardHeader>
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-14 h-14 bg-blue-950/30 backdrop-blur-sm rounded-lg flex items-center justify-center">
                      <FileText className="text-white" size={28} />
                    </div>
                    <Badge variant="outline" className="border-[#1e3a5f] text-white">
                      {doc.fileType}
                    </Badge>
                  </div>
                  <CardTitle className="text-xl text-white">{doc.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-blue-200 mb-4 min-h-[60px]">
                    {doc.description}
                  </CardDescription>
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-sm text-blue-300">Size: {doc.size}</span>
                  </div>
                  <Button 
                    onClick={() => handleDownload(doc)}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                  >
                    <Download className="mr-2" size={18} />
                    Download
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Document Categories */}
      <section className="py-16 bg-blue-900/20 backdrop-blur-sm">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">Document Categories</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="border-blue-500/30">
                <CardHeader>
                  <CardTitle className="text-white">Company Introduction</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-blue-100">
                    <li className="flex items-start space-x-2">
                      <span className="text-white mt-1">•</span>
                      <span>Capability Statement</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-white mt-1">•</span>
                      <span>Quad Chart</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-white mt-1">•</span>
                      <span>Company Briefing Materials</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="border-blue-500/30">
                <CardHeader>
                  <CardTitle className="text-white">Technical Overview</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-blue-100">
                    <li className="flex items-start space-x-2">
                      <span className="text-white mt-1">•</span>
                      <span>Defense Research Brief</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-white mt-1">•</span>
                      <span>Public Patent Portfolio Overview</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Usage Guidelines */}
      <section className="py-16 bg-transparent">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <Card className="border-[#1e3a5f]">
              <CardHeader>
                <CardTitle className="text-2xl text-white">Document Usage Guidelines</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-blue-950/30 backdrop-blur-sm rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-blue-100">
                    <strong>Public Distribution:</strong> All documents in this section are cleared for 
                    public distribution and may be shared for partnership discussions, government outreach, 
                    or technical collaboration purposes.
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-blue-950/30 backdrop-blur-sm rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-blue-100">
                    <strong>Technical Depth:</strong> Documents provide public-safe technical overviews 
                    without exposing sensitive implementation details or proprietary methodologies.
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-blue-950/30 backdrop-blur-sm rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-blue-100">
                    <strong>Additional Information:</strong> For more detailed technical specifications 
                    or confidential capability briefings, please contact SALAR directly through our 
                    secure channels.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Request Additional Materials */}
      <section className="py-16 bg-gradient-to-r from-[#0a1628] to-[#1e3a5f] text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-6">Need Additional Materials?</h2>
            <p className="text-lg text-slate-300 mb-8">
              For confidential capability briefings, detailed technical specifications, or custom partnership 
              materials, please contact SALAR directly.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="mailto:contact@salartechnologies.com">
                <Button size="lg" className="bg-blue-900/20 backdrop-blur-sm text-white hover:bg-slate-100">
                  Request Materials
                </Button>
              </a>
              <a href="mailto:vikrant@salartechnologies.com">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-blue-900/20 backdrop-blur-sm/10">
                  Direct Contact
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Documents;
